import { useState, type ReactNode } from 'react';
import { Alert, Box, IconButton, Typography, Collapse, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon, InfoOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations.tsx';

/** Same width as the slot list row right column (`Item` publish / icons area). */
export const SLOT_LIST_ROW_RIGHT_SECTION_WIDTH_PX = 65;

interface CollapsibleInfoAlertProps {
  title: string;
  description: string;
  /** Right-aligned; not part of the expand/collapse click target. */
  titleEnd?: ReactNode;
  sx?: SxProps<Theme>;
  /** Fires when the user opens or closes the expanded panel (e.g. parent can reclaim horizontal space). */
  onExpandedChange?: (expanded: boolean) => void;
  /** Schedule tab: borderless, no expand chevron — title row still toggles description. */
  compact?: boolean;
}

const CollapsibleInfoAlert = ({
  title,
  description,
  titleEnd,
  sx,
  onExpandedChange,
  compact = false,
}: CollapsibleInfoAlertProps) => {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandedChange?.(next);
  };

  const handleClose = () => {
    setExpanded(false);
    onExpandedChange?.(false);
  };

  const rootSx: SxProps<Theme> =
    sx === undefined ? { mt: 1 } : Array.isArray(sx) ? [{ mt: 1 }, ...sx] : [{ mt: 1 }, sx];

  return (
    <Box sx={rootSx}>
      <Alert
        severity="info"
        variant={compact ? 'standard' : 'outlined'}
        icon={false}
        sx={{
          ...(compact && {
            py: 0,
            px: 0,
            border: 'none',
            backgroundColor: 'transparent',
            alignItems: 'center',
          }),
          '& .MuiAlert-message': {
            width: '100%',
            padding: 0,
          },
        }}
      >
        <Stack spacing={compact ? 0 : 0.5} sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={compact ? 0.5 : 1} sx={{ width: '100%' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={compact ? 0.5 : 1}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-label={expanded ? t('common.collapseInfo') : t('common.expandInfo')}
              onClick={handleToggle}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleToggle();
                }
              }}
              sx={{
                'flex': 1,
                'minWidth': 0,
                'cursor': 'pointer',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              <InfoOutlined sx={{ color: 'info.main', flexShrink: 0 }} fontSize="small" />
              <Typography variant="alertDescription" sx={{ flex: 1, minWidth: 0 }}>
                {title}
              </Typography>
            </Stack>
            {titleEnd}
            {!compact ? (
              <IconButton
                aria-label={expanded ? t('common.collapseInfo') : t('common.expandInfo')}
                aria-expanded={expanded}
                color="inherit"
                size="small"
                onClick={expanded ? handleClose : handleToggle}
                sx={{ flexShrink: 0 }}
              >
                {expanded ? <CloseIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            ) : null}
          </Stack>
          <Collapse in={expanded}>
            <Typography variant="body2" sx={{ display: 'block' }}>
              {description}
            </Typography>
          </Collapse>
        </Stack>
      </Alert>
    </Box>
  );
};

export default CollapsibleInfoAlert;
